import { useMemo } from 'react'
import { z } from 'zod'
import { useLocalStorage } from '@hooks/useLocalStorage.ts'
import {
	Cuisine,
	type Cuisine as TCuisine,
	PrimaryCarb,
	type PrimaryCarb as TPrimaryCarb,
	PrimaryProtein,
	type PrimaryProtein as TPrimaryProtein,
	type Recipe,
	SauceBase,
	type SauceBase as TSauceBase,
} from '@hewey/shared'
import { totalMins } from './time.ts'

// Time buckets by total (prep + cook) minutes. Mirror the Explore collections;
// quick ⊂ weeknight by design (see the "keep quick too" decision). Recipes with
// no published time satisfy none of them.
export const TIME_BUCKETS = ['quick', 'weeknight', 'weekend'] as const
export const TimeBucket = z.enum(TIME_BUCKETS)
export type TimeBucket = z.infer<typeof TimeBucket>

export const TIME_BUCKET_LABEL: Record<TimeBucket, string> = {
	quick: 'Under 30 min',
	weeknight: 'Under 45 min',
	weekend: 'Over 45 min',
}

export const inTimeBucket = (mins: number, bucket: TimeBucket): boolean =>
	bucket === 'quick'
		? mins <= 30
		: bucket === 'weeknight'
			? mins <= 45
			: mins > 45

// Filter on the meaningful systemTags facets plus a total-time bucket. Season
// is omitted (its all_year default makes exact-match filtering confusing).
export type RecipeFilter = {
	cuisines: TCuisine[]
	proteins: TPrimaryProtein[]
	carbs: TPrimaryCarb[]
	sauces: TSauceBase[]
	times: TimeBucket[]
}

export const DEFAULT_RECIPE_FILTER: RecipeFilter = {
	cuisines: [],
	proteins: [],
	carbs: [],
	sauces: [],
	times: [],
}

// Loose schema — older/unknown shapes collapse to the default rather than
// throwing.
export const RecipeFilterSchema = z.object({
	cuisines: z.array(Cuisine).default([]),
	proteins: z.array(PrimaryProtein).default([]),
	carbs: z.array(PrimaryCarb).default([]),
	sauces: z.array(SauceBase).default([]),
	times: z.array(TimeBucket).default([]),
})

export const isDefaultRecipeFilter = (f: RecipeFilter): boolean =>
	f.cuisines.length === 0 &&
	f.proteins.length === 0 &&
	f.carbs.length === 0 &&
	f.sauces.length === 0 &&
	f.times.length === 0

export const activeFilterCount = (f: RecipeFilter): number =>
	f.cuisines.length +
	f.proteins.length +
	f.carbs.length +
	f.sauces.length +
	f.times.length

export const matchesFilter = (r: Recipe, f: RecipeFilter): boolean => {
	if (isDefaultRecipeFilter(f)) return true
	// Untagged recipes (pending/failed) can't satisfy a tag filter.
	const t = r.systemTags
	if (!t) return false
	if (f.cuisines.length > 0 && !f.cuisines.includes(t.cuisine)) return false
	if (f.proteins.length > 0 && !f.proteins.includes(t.primaryProtein))
		return false
	if (f.carbs.length > 0 && !f.carbs.includes(t.primaryCarb)) return false
	if (f.sauces.length > 0 && !f.sauces.includes(t.sauceBase)) return false
	if (f.times.length > 0) {
		const mins = totalMins(r)
		if (mins === null) return false
		if (!f.times.some((b) => inTimeBucket(mins, b))) return false
	}
	return true
}

const FILTER_KEY = 'hewey.recipes.filter'

export const useRecipeFilter = (): [
	RecipeFilter,
	(next: RecipeFilter) => void,
] => {
	const [stored, setStored] = useLocalStorage<unknown>(FILTER_KEY)
	const filter = useMemo<RecipeFilter>(() => {
		const parsed = RecipeFilterSchema.safeParse(stored)
		return parsed.success ? parsed.data : DEFAULT_RECIPE_FILTER
	}, [stored])
	const setFilter = (next: RecipeFilter) =>
		setStored(isDefaultRecipeFilter(next) ? undefined : next)
	return [filter, setFilter]
}
